import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import API_BASE_URL from "../config";
import { useJobsContext } from "../contexts/JobsProvider";
import { isRunningStatus, isFailureStatus, isDoneStatus } from "../utils/jobStatus";
import { AGENT_LABELS as LABELS } from "../utils/agentLabels";
import "./ActivityMenu.css";

/**
 * Global, always-top-right activity area. A bell-style button (with a live count of
 * running jobs) opens a dropdown showing what's running now and the
 * completed history, from anywhere in the app. Clicking a running job
 * opens its progress; a finished run opens its result; runs can be deleted inline.
 */
export default function ActivityMenu({ onOpenJob, onOpenRun, refreshKey }) {
  const { t } = useTranslation();
  const { jobs, available } = useJobsContext();
  const [open, setOpen] = useState(false);
  const [runs, setRuns] = useState([]);
  const [confirmId, setConfirmId] = useState(null);
  const ref = useRef(null);
  const prevDone = useRef(0);

  // Status-vocabulary-aware so takeoff (pending/running/error) categorizes alongside
  // the generic jobs (running/done/failed) — one activity surface for all agents.
  const running = jobs.filter((j) => isRunningStatus(j.status));
  const failed = jobs.filter((j) => isFailureStatus(j.status));
  const activeItems = [...running, ...failed];

  const loadRuns = useCallback(() => {
    fetch(`${API_BASE_URL}/api/v1/runs`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setRuns(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  useEffect(() => { loadRuns(); }, [loadRuns, refreshKey]);

  // Completed jobs auto-save to history → refresh the run list when one finishes.
  useEffect(() => {
    const done = jobs.filter((j) => isDoneStatus(j.status) || isFailureStatus(j.status)).length;
    if (done > prevDone.current) loadRuns();
    prevDone.current = done;
  }, [jobs, loadRuns]);

  // Close the dropdown on an outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const deleteRun = (id) => {
    setConfirmId(null);
    setRuns((prev) => prev.filter((r) => r.id !== id));
    fetch(`${API_BASE_URL}/api/v1/runs/${id}`, { method: "DELETE" }).catch(loadRuns);
  };

  if (!available) return null; // jobs/history backend not available

  // Compact always-visible label of what's running right now (no click needed).
  const runningLabel = running.length === 1
    ? (running[0].message
        ? `${LABELS[running[0].agent_type] || running[0].agent_type} · ${running[0].message}`
        : t("activity.jobRunningLabel", { label: LABELS[running[0].agent_type] || running[0].agent_type }))
    : running.length > 1
    ? t("activity.multipleJobsRunning", { count: running.length })
    : "";

  return (
    <div className="activity-menu" ref={ref}>
      {running.length > 0 && (
        <button className="activity-pill" onClick={() => setOpen(true)}
                title={t("activity.viewRunningJobs")}>
          <span className="activity-spin act-spin-running" aria-hidden="true" />
          <span className="activity-pill-text">{runningLabel}</span>
        </button>
      )}
      <button
        className={`activity-btn${running.length ? " busy" : ""}`}
        onClick={() => setOpen((v) => !v)}
        title={t("activity.title")}
        aria-label={t("activity.title")}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
        {running.length > 0 && <span className="activity-badge">{running.length}</span>}
        {running.length === 0 && failed.length > 0 && (
          <span className="activity-badge activity-badge-err">!</span>
        )}
      </button>

      {open && (
        <div className="activity-panel">
          <div className="activity-head">{t("activity.title")}</div>

          <div className="activity-section-label">{t("activity.runningNow")}</div>
          {activeItems.length === 0 ? (
            <div className="activity-empty">{t("activity.noActiveJobs")}</div>
          ) : (
            activeItems.map((job) => {
              // Canonical class so takeoff's pending/error reuse the running/failed styles.
              const kind = isFailureStatus(job.status) ? "failed" : "running";
              return (
                <div
                  key={job.id}
                  className={`activity-item act-${kind}`}
                  onClick={() => { onOpenJob?.(job); setOpen(false); }}
                >
                  <span className={`activity-spin act-spin-${kind}`} aria-hidden="true" />
                  <div className="activity-text">
                    <div className="activity-title">
                      {LABELS[job.agent_type] || job.agent_type}
                      <span className={`activity-status status-${kind}`}>
                        {kind === "failed" ? t("activity.failed") : t("activity.running")}
                      </span>
                    </div>
                    <div className="activity-sub">
                      {kind === "failed"
                        ? (job.error || t("activity.failed"))
                        : job.message
                        ? `${job.project_name} · ${job.message}`
                        : job.project_name}
                    </div>
                  </div>
                </div>
              );
            })
          )}

          <div className="activity-section-label">{t("activity.history")}</div>
          {runs.length === 0 ? (
            <div className="activity-empty">{t("activity.noRunsYet")}</div>
          ) : (
            runs.slice(0, 25).map((run) => (
              <div
                key={run.id}
                className="activity-item activity-run"
                onClick={() => { if (confirmId !== run.id) { onOpenRun?.(run.id); setOpen(false); } }}
              >
                <span className={`activity-verdict ${run.verdict ? "activity-ok" : "activity-fail"}`}>
                  {run.verdict ? "✓" : "✗"}
                </span>
                <div className="activity-text">
                  <div className="activity-title">{LABELS[run.agent_type] || run.workflow_id}</div>
                  <div className="activity-sub">{run.project_name}</div>
                </div>
                {confirmId === run.id ? (
                  <span className="activity-confirm" onClick={(e) => e.stopPropagation()}>
                    <button className="ac-yes"
                      onClick={(e) => { e.stopPropagation(); deleteRun(run.id); }}>{t("sidebar.delete")}</button>
                    <button className="ac-no"
                      onClick={(e) => { e.stopPropagation(); setConfirmId(null); }}>{t("sidebar.cancel")}</button>
                  </span>
                ) : (
                  <button className="activity-del" title={t("sidebar.removeFromHistory")}
                    onClick={(e) => { e.stopPropagation(); setConfirmId(run.id); }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6l-1 14H6L5 6" />
                      <path d="M10 11v6M14 11v6" />
                      <path d="M9 6V4h6v2" />
                    </svg>
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
